import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const CorrectionsList = ({ corrections, onUpdateStatus, loading }) => {
  const getPriorityColor = (priority) => {
    const colors = {
      immediate: 'text-error',
      next_pay_period: 'text-warning',
      next_month: 'text-primary',
      scheduled: 'text-info'
    };
    return colors?.[priority] || 'text-muted-foreground';
  };

  const getPriorityBg = (priority) => {
    const backgrounds = {
      immediate: 'bg-error/10',
      next_pay_period: 'bg-warning/10',
      next_month: 'bg-primary/10',
      scheduled: 'bg-info/10'
    };
    return backgrounds?.[priority] || 'bg-muted';
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'text-warning',
      in_progress: 'text-primary',
      completed: 'text-success',
      failed: 'text-error',
      cancelled: 'text-muted-foreground'
    };
    return colors?.[status] || 'text-muted-foreground';
  };

  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return 'K0.00';
    return `K${amount?.toFixed(2)?.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Icon name="Loader2" size={48} color="var(--color-primary)" className="animate-spin" />
      </div>
    );
  }

  if (!corrections || corrections?.length === 0) {
    return (
      <div className="py-12 text-center">
        <Icon name="Sparkles" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-3 opacity-50" />
        <p className="caption text-sm text-muted-foreground">No AI corrections available</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {corrections?.map((correction) => (
        <div key={correction?.id} className="border border-border rounded-lg p-4 hover:shadow-sm transition-smooth">
          <div className="flex items-start justify-between gap-2 mb-3">
            <div>
              <h4 className="font-heading font-semibold text-sm text-foreground mb-1">
                {correction?.employee?.firstName} {correction?.employee?.lastName}
              </h4>
              <p className="caption text-xs text-muted-foreground">
                {correction?.employee?.employeeNumber} • {correction?.employee?.department}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <span className={`px-2 py-1 ${getPriorityBg(correction?.priority)} ${getPriorityColor(correction?.priority)} text-xs font-caption rounded-full capitalize`}>
                {correction?.priority?.replace(/_/g, ' ')}
              </span>
              <span className={`px-2 py-1 bg-muted ${getStatusColor(correction?.implementationStatus)} text-xs font-caption rounded-full capitalize`}>
                {correction?.implementationStatus?.replace(/_/g, ' ')}
              </span>
            </div>
          </div>

          <div className="bg-muted/50 rounded-lg p-3 mb-3">
            <p className="caption text-xs font-medium text-foreground mb-2 capitalize">
              {correction?.correctionType?.replace(/_/g, ' ')}
            </p>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <p className="caption text-xs text-muted-foreground mb-1">Current Value</p>
                <p className="font-heading font-medium text-sm text-error">
                  {formatCurrency(correction?.currentValue)}
                </p>
              </div>
              <div>
                <p className="caption text-xs text-muted-foreground mb-1">Corrected Value</p>
                <p className="font-heading font-medium text-sm text-success">
                  {formatCurrency(correction?.correctedValue)}
                </p>
              </div>
              <div>
                <p className="caption text-xs text-muted-foreground mb-1">Backpay Amount</p>
                <p className="font-heading font-medium text-sm text-warning">
                  {formatCurrency(correction?.backpayAmount)}
                </p>
              </div>
            </div>
          </div>

          <div className="p-3 bg-primary/5 border-l-4 border-primary rounded mb-3">
            <div className="flex items-start gap-2">
              <Icon name="Calculator" size={16} color="var(--color-primary)" className="mt-0.5" />
              <div className="flex-1">
                <p className="caption text-xs font-medium text-foreground mb-1">Calculation Method</p>
                <p className="caption text-xs text-muted-foreground">{correction?.calculationMethod}</p>
              </div>
            </div>
          </div>

          {correction?.aiConfidenceScore && (
            <div className="flex items-center gap-2 mb-3">
              <Icon name="Sparkles" size={14} color="var(--color-primary)" />
              <p className="caption text-xs text-muted-foreground">
                AI Confidence: <span className="font-medium text-foreground">{(correction?.aiConfidenceScore * 100)?.toFixed(0)}%</span>
              </p>
            </div>
          )}

          {correction?.notes && (
            <p className="caption text-xs text-muted-foreground mb-3 italic">
              Note: {correction?.notes}
            </p>
          )}

          <div className="flex items-center gap-2">
            {correction?.implementationStatus === 'pending' && (
              <>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => onUpdateStatus?.(correction?.id, 'in_progress')}
                >
                  Start Implementation
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onUpdateStatus?.(correction?.id, 'cancelled')}
                >
                  Reject
                </Button>
              </>
            )}
            {correction?.implementationStatus === 'in_progress' && (
              <Button
                variant="success"
                size="sm"
                onClick={() => onUpdateStatus?.(correction?.id, 'completed', { reviewedBy: 'Current User' })}
              >
                Mark Complete
              </Button>
            )}
            {correction?.implementationStatus === 'completed' && correction?.implementedAt && (
              <div className="flex items-center gap-2 text-success">
                <Icon name="CheckCircle2" size={16} color="currentColor" />
                <p className="caption text-xs">
                  Implemented on {new Date(correction?.implementedAt)?.toLocaleDateString('en-PG')}
                </p>
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

export default CorrectionsList;